import React, { useRef, useState } from "react";
import { compactNum } from "../../utils/bettorsDeskHelpers";

const clamp01 = (v) => Math.min(1, Math.max(0, v));

/** Stepped concentration bars: top 1 / 3 / 5 share of handle. */
export const WhaleConcentrationBars = ({ concentration, compact = false }) => {
  if (!concentration?.loaded) return null;
  const { tiers } = concentration;

  const toneFor = (share) =>
    share > 60 ? "down" : share > 40 ? "warn" : "accent";

  return (
    <ul className={`bd-whale-tiers${compact ? " bd-whale-tiers-compact" : ""}`}>
      {tiers.map((tier) => {
        const tone = toneFor(tier.share);
        return (
          <li key={tier.key} className="bd-whale-tier">
            <div className="bd-whale-tier-meta">
              <span>{tier.label}</span>
              <strong className={`tabular-nums bd-tone-${tone}`}>{tier.share}%</strong>
            </div>
            <div className="bd-whale-track" aria-hidden>
              <span
                className={`bd-whale-fill bd-whale-fill-${tone}`}
                style={{ width: `${Math.max(tier.share, 2)}%` }}
              />
            </div>
            {!compact ? (
              <span className="bd-whale-hint tabular-nums">
                {compactNum(tier.stake)} of {compactNum(concentration.handle)} handle
              </span>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
};

const HIST_W = 260;
const HIST_H = 72;
const HIST_PT = 8;
const HIST_PB = 16;

export const BettorStakeHistogram = ({ histogram, ariaLabel, compact = false }) => {
  const surfaceRef = useRef(null);
  const [hoverIndex, setHoverIndex] = useState(null);

  if (!histogram?.loaded) return null;
  const { buckets, maxCount } = histogram;
  const n = buckets.length;
  const h = compact ? HIST_H : 118;
  const pt = compact ? HIST_PT : 12;
  const pb = compact ? HIST_PB : 20;
  const innerH = h - pt - pb;
  const slot = HIST_W / n;
  const barW = Math.min(slot * 0.55, compact ? 28 : 44);

  const handleMove = (event) => {
    if (!surfaceRef.current) return;
    const rect = surfaceRef.current.getBoundingClientRect();
    const frac = clamp01((event.clientX - rect.left) / rect.width);
    setHoverIndex(Math.min(n - 1, Math.floor(frac * n)));
  };

  const hover = hoverIndex != null ? buckets[hoverIndex] : null;
  const hoverXFrac = hoverIndex != null ? (hoverIndex + 0.5) / n : 0;

  return (
    <div
      ref={surfaceRef}
      className="bd-hist-surface"
      onPointerMove={handleMove}
      onPointerLeave={() => setHoverIndex(null)}
    >
      <svg
        viewBox={`0 0 ${HIST_W} ${h}`}
        preserveAspectRatio="none"
        className="bd-hist-svg"
        style={compact ? { height: h } : undefined}
        role="img"
        aria-label={ariaLabel}
      >
        {[0.5, 1].map((ratio) => (
          <line
            key={ratio}
            x1={0}
            x2={HIST_W}
            y1={pt + innerH * (1 - ratio)}
            y2={pt + innerH * (1 - ratio)}
            className="bd-hist-grid"
            vectorEffect="non-scaling-stroke"
          />
        ))}
        {buckets.map((bucket, i) => {
          const barH = bucket.count > 0 ? Math.max((bucket.count / maxCount) * innerH, 2) : 0;
          if (barH === 0) return null;
          return (
            <rect
              key={bucket.key}
              x={(i + 0.5) * slot - barW / 2}
              y={pt + innerH - barH}
              width={barW}
              height={barH}
              rx={2}
              className="bd-hist-bar"
              opacity={hoverIndex == null || hoverIndex === i ? 1 : 0.4}
            />
          );
        })}
        {hover ? (
          <line
            x1={hoverXFrac * HIST_W}
            x2={hoverXFrac * HIST_W}
            y1={pt}
            y2={h - pb}
            className="bd-hist-crosshair"
            vectorEffect="non-scaling-stroke"
          />
        ) : null}
      </svg>

      <div className="bd-hist-labels" aria-hidden>
        {buckets.map((bucket) => (
          <span key={bucket.key}>{bucket.label}</span>
        ))}
      </div>

      {hover ? (
        <div
          className={`bd-hist-tooltip tabular-nums${hoverXFrac > 0.68 ? " bd-hist-tooltip-left" : ""}`}
          style={{ left: `${hoverXFrac * 100}%` }}
        >
          <em>{hover.label}</em>
          <strong>
            {hover.count} bettor{hover.count === 1 ? "" : "s"}
          </strong>
        </div>
      ) : null}
    </div>
  );
};

export const BettorRecencyBar = ({ split, ariaLabel, compact = false }) => {
  if (!split?.loaded || split.total <= 0) return null;
  const visible = split.segments.filter((seg) => seg.count > 0);
  if (!visible.length) return null;

  return (
    <div className="bd-recency" role="img" aria-label={ariaLabel}>
      <div className="bd-recency-bar" aria-hidden>
        {visible.map((seg) => (
          <span
            key={seg.key}
            className={`bd-recency-seg bd-recency-seg-${seg.tone}`}
            style={{ width: `${(seg.count / split.total) * 100}%` }}
          />
        ))}
      </div>
      <ul className={`bd-recency-legend${compact ? " bd-recency-legend-compact" : ""}`}>
        {split.segments.map((seg) => (
          <li key={seg.key}>
            <i className={`bd-recency-dot bd-recency-seg-${seg.tone}`} aria-hidden />
            {seg.label}
            <strong className="tabular-nums">{seg.count}</strong>
            {!compact ? (
              <em className="tabular-nums">{Math.round((seg.count / split.total) * 100)}%</em>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
};
